import { asyncComponent, AsyncRouteProps } from '@jaredpalmer/after';
import pathToRegexp from 'path-to-regexp';
import qs from 'qs';
import { match as Match } from 'react-router-dom';
import Placeholder from './AppComponentLoader';
import { SupportedLocale } from './i18n/types';
import {
  activeFavoriteProducts,
  customerProfileNavigationItems,
  showContactPage,
  showDownloadDocuments,
} from './config';

/**
 * Standard routes builder...
 * @param locale
 * @param pathe
 * @param otherParamsToQs
 * @param inputParams
 */
export function routesBuilderDefaultdUrlBuilder(
  locale: SupportedLocale,
  path: string,
  otherParamsToQs: boolean | string[] | undefined | null,
  inputParams: Record<string, any> | undefined,
): string {
  const { pathParams, queryParams, otherParamsExists } = routesBuilderSplitInputParams(
    locale,
    otherParamsToQs,
    path,
    inputParams || {},
  );

  const toUrl = pathToRegexp.compile(path);
  const urlB = toUrl(pathParams);

  return !otherParamsToQs || !otherParamsExists
    ? safeAbsoluteUrl(path, urlB)
    : safeAbsoluteUrl(
        path,
        `${urlB}${qs.stringify(queryParams, {
          addQueryPrefix: true,
        })}`,
      );
}

export function pathToRouteUrlType(path: string): RouteUrlType | null {
  for (const key in routesDefinition) {
    const def = routesDefinition[key as RouteUrlType];
    if (def && typeof def.path !== 'object') continue;

    const paths = Object.values(def?.path || {}) as string[];
    if (paths.includes(path)) return key as RouteUrlType;
  }

  return null;
}

export const routesDefinition: {
  [key: string]: Omit<AsyncRouteProps<any>, 'path'> & {
    path: Record<SupportedLocale, string> | string;
    urlBuilder?: (
      locale: SupportedLocale,
      path: string,
      otherParamsToQs: boolean | string[] | undefined | null,
      inputParams: Record<string, any> | undefined,
    ) => string;
  };
} = {
  catalogue: {
    path: {
      cs: '/catalogue',
      en: '/catalogue',
      hu: '/catalogue',
      de: '/catalogue',
      sk: '/catalogue',
      hr: '/catalogue',
      sl: '/catalogue',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CataloguePage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },

  ...(activeFavoriteProducts
    ? {
        favoriteProducts: {
          path: {
            cs: '/catalogue/favorite',
            en: '/catalogue/favorite',
            hu: '/catalogue/favorite',
            de: '/catalogue/favorite',
            sk: '/catalogue/favorite',
            hr: '/catalogue/favorite',
            sl: '/catalogue/favorite',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/FavoriteProductsPage'),
            Placeholder,
          }),
        },
      }
    : {}),
  categories: {
    path: {
      cs: '/catalogue/:categories*',
      en: '/catalogue/:categories*',
      hu: '/catalogue/:categories*',
      de: '/catalogue/:categories*',
      sk: '/catalogue/:categories*',
      hr: '/catalogue/:categories*',
      sl: '/catalogue/:categories*',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CataloguePage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  loyaltyProgram: {
    path: {
      cs: '/vernostni-program',
      en: '/loyalty-program',
      hu: '/loyalty-program',
      de: '/loyalty-program',
      sk: '/vernostny-program',
      hr: '/loyalty-program',
      sl: '/loyalty-program',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/LoyaltyProgramPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  loyaltyProgramCategories: {
    path: {
      cs: '/vernostni-program/:categories*',
      en: '/loyalty-program/:categories*',
      hu: '/loyalty-program/:categories*',
      de: '/loyalty-program/:categories*',
      sk: '/vernostny-program/:categories*',
      hr: '/loyalty-program/:categories*',
      sl: '/loyalty-program/:categories*',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/LoyaltyProgramPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  special: {
    path: {
      cs: '/special/:categories*',
      en: '/special/:categories*',
      hu: '/specialis/:categories*',
      de: '/spezial/:categories*',
      sk: '/special/:categories*',
      hr: '/special/:categories*',
      sl: '/special/:categories*',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CataloguePage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  product: {
    path: {
      cs: '/:sn([^/]+)-product-:id([^/]+)',
      en: '/:sn([^/]+)-product-:id([^/]+)',
      hu: '/:sn([^/]+)-product-:id([^/]+)',
      de: '/:sn([^/]+)-product-:id([^/]+)',
      sk: '/:sn([^/]+)-product-:id([^/]+)',
      hr: '/:sn([^/]+)-product-:id([^/]+)',
      sl: '/:sn([^/]+)-product-:id([^/]+)',
    },
    component: asyncComponent({
      loader: () => import('./pages/ProductDetailPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  bulkPurchase: {
    path: {
      cs: '/hromadny-nakup',
      en: '/bulk-purchase',
      hu: '/nagybani-beszerzese',
      de: '/masseneinkauf',
      sk: '/hromadny-nakup',
      hr: '/hromadny-nakup',
      sl: '/hromadny-nakup',
    },
    component: asyncComponent({
      loader: () => import('./pages/BulkPurchasePage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(showDownloadDocuments
    ? {
        downloadDocuments: {
          path: {
            cs: '/dokumenty-ke-stazeni',
            en: '/documents-to-download',
            hu: '/dokumentumok-letoltese',
            de: '/dokumente-zum-download',
            sk: '/dokumenty-na-stiahnutie',
            hr: '/dokumenty-na-stiahnutie',
            sl: '/dokumenty-na-stiahnutie',
          },
          component: asyncComponent({
            loader: () => import('./pages/DownloadDocumentsPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  cartSummary: {
    path: {
      cs: '/cart/summary',
      en: '/cart/summary',
      hu: '/cart/osszefoglalo',
      de: '/cart/zusammenfassung',
      sk: '/cart/zhrnutie',
      hr: '/cart/zhrnutie',
      sl: '/cart/zhrnutie',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CartSummaryPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  orderThankYou: {
    path: {
      cs: '/dekujeme-za-objednavku',
      en: '/thanks',
      hu: '/koszonjuk',
      de: '/danke',
      sk: '/vdaka-za-objednavku',
      hr: '/vdaka-za-objednavku',
      sl: '/vdaka-za-objednavku',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/OrderThankYouPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  orderSentToApproval: {
    path: {
      cs: '/objednavka-odeslana-ke-schvaleni',
      en: '/order-sent-to-approval',
      hu: '/rendeles-elkuldve-jovahagyasra',
      de: '/bestellung-gesendet-zur-genehmigung',
      sk: '/objednavka-odoslana-na-schvalenie',
      hr: '/objednavka-odoslana-na-schvalenie',
      sl: '/objednavka-odoslana-na-schvalenie',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/OrderSentToApprovalPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(customerProfileNavigationItems.includes('cartsApproval')
    ? {
        cartsApproval: {
          path: {
            cs: '/profil/schvaleni-objednavek',
            en: '/profile/orders-approval',
            hu: '/profil/megrendelesek-jovahagyas',
            de: '/profil/auftragsbestatigung',
            sk: '/profil/schvalenie-objednavok',
            hr: '/profil/schvalenie-objednavok',
            sl: '/profil/schvalenie-objednavok',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CartsApprovalPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  cartApproval: {
    path: {
      cs: '/profil/schvaleni-objednavek/:id',
      en: '/profile/orders-approval/:id',
      hu: '/profil/megrendelesek-jovahagyas/:id',
      de: '/profil/auftragsbestatigung/:id',
      sk: '/profil/schvalenie-objednavok/:id',
      hr: '/profil/schvalenie-objednavok/:id',
      sl: '/profil/schvalenie-objednavok/:id',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CartApprovalPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(customerProfileNavigationItems.includes('customerProfile')
    ? {
        customerProfile: {
          path: {
            cs: '/profil',
            en: '/profile',
            hu: '/profil',
            de: '/profil',
            sk: '/profil',
            hr: '/profil',
            sl: '/profil',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfilePage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  ...(customerProfileNavigationItems.includes('customerProfileOrders')
    ? {
        customerProfileOrders: {
          path: {
            cs: '/profil/objednavky',
            en: '/profile/orders',
            hu: '/profil/parancsokat',
            de: '/profil/auftrage',
            sk: '/profil/objednavky',
            hr: '/profil/objednavky',
            sl: '/profil/objednavky',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileOrdersPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  customerProfileOrderDetail: {
    path: {
      cs: '/profil/objednavky/:id',
      en: '/profile/orders/:id',
      hu: '/profil/parancsokat/:id',
      de: '/profil/auftrage/:id',
      sk: '/profil/objednavky/:id',
      hr: '/profil/objednavky/:id',
      sl: '/profil/objednavky/:id',
    },
    component: asyncComponent({
      loader: () => import('./pages/CustomerProfileOrderDetailPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(customerProfileNavigationItems.includes('customerProfileInvoices')
    ? {
        customerProfileInvoices: {
          path: {
            cs: '/profil/faktury',
            en: '/profile/invoices',
            hu: '/profil/szamlakat',
            de: '/profil/rechnungen',
            sk: '/profil/faktury',
            hr: '/profil/faktury',
            sl: '/profil/faktury',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileInvoicesPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  customerProfileInvoiceDetail: {
    path: {
      cs: '/profil/faktury/:id',
      en: '/profile/invoices/:id',
      hu: '/profil/szamlakat/:id',
      de: '/profil/rechnungen/:id',
      sk: '/profil/faktury/:id',
      hr: '/profil/faktury/:id',
      sl: '/profil/faktury/:id',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CustomerProfileInvoiceDetailPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(customerProfileNavigationItems.includes('customerProfileCreditNotes')
    ? {
        customerProfileCreditNotes: {
          path: {
            cs: '/profil/dobropisy',
            en: '/profile/credit-notes',
            hu: '/profil/hitel-jegyzetek',
            de: '/profil/gutschriften',
            sk: '/profil/dobropisy',
            hr: '/profil/dobropisy',
            sl: '/profil/dobropisy',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileCreditNotesPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  ...(customerProfileNavigationItems.includes('customerProfileInvoicesAndCreditNotes')
    ? {
        customerProfileInvoicesAndCreditNotes: {
          path: {
            cs: '/profil/faktury-a-dobropisy',
            en: '/profile/invoices-and-credit-notes',
            hu: '/profil/szamlak-es-jovairasok',
            de: '/profil/rechnungen-und-gutschriften',
            sk: '/profil/faktury-a-dobropisy',
            hr: '/profil/faktury-a-dobropisy',
            sl: '/profil/faktury-a-dobropisy',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileInvoicesAndCreditNotesPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  customerProfileCreditNoteDetail: {
    path: {
      cs: '/profil/dobropisy/:id',
      en: '/profile/credit-notes/:id',
      hu: '/profil/hitel-jegyzetek/:id',
      de: '/profil/gutschriften/:id',
      sk: '/profil/dobropisy/:id',
      hr: '/profil/dobropisy/:id',
      sl: '/profil/dobropisy/:id',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/CustomerProfileCreditNoteDetailPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(customerProfileNavigationItems.includes('customerProfileDocuments')
    ? {
        customerProfileDocuments: {
          path: {
            cs: '/profil/dokumenty',
            en: '/profile/documents',
            hu: '/profil/dokumentumokat',
            de: '/profil/unterlagen',
            sk: '/profil/dokumenty',
            hr: '/profil/dokumenty',
            sl: '/profil/dokumenty',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileDocumentsPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  ...(customerProfileNavigationItems.includes('customerProfileDeliveryNotes')
    ? {
        customerProfileDeliveryNotes: {
          path: {
            cs: '/profil/dodaci-listy',
            en: '/profile/delivery-notes',
            hu: '/profil/szallitolevelek',
            de: '/profil/lieferscheine',
            sk: '/profil/dodaci-listy',
            hr: '/profil/dodaci-listy',
            sl: '/profil/dodaci-listy',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileDeliveryNotesPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  ...(customerProfileNavigationItems.includes('customerProfileShoppingLists')
    ? {
        customerProfileShoppingLists: {
          path: {
            cs: '/profil/nakupni-seznamy',
            en: '/profile/shopping-lists',
            hu: '/profil/bevasarlo-listak',
            de: '/profil/einkaufslisten',
            sk: '/profil/nakupne-zoznamy',
            hr: '/profil/nakupne-zoznamy',
            sl: '/profil/nakupne-zoznamy',
          },
          exact: true,
          component: asyncComponent({
            loader: () => import('./pages/CustomerProfileShoppingListsPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  homePage: {
    path: {
      cs: '/domovska-stranka',
      en: '/home',
      hu: '/kezdolap',
      de: '/startseite',
      sk: '/domovskej-stranke',
      hr: '/domovskej-stranke',
      sl: '/domovskej-stranke',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/HomePage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  staticPage: {
    path: '/page/:group',
    component: asyncComponent({
      loader: () => import('./pages/StaticPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  news: {
    path: {
      cs: '/aktuality',
      en: '/news',
      hu: '/hirek',
      de: '/nachricht',
      sk: '/spravy',
      hr: '/spravy',
      sl: '/spravy',
    },
    component: asyncComponent({
      loader: () => import('./pages/NewsPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  career: {
    path: {
      cs: '/kariera',
      en: '/career',
      hu: '/karrier',
      de: '/karriere',
      sk: '/kariera',
      hr: '/kariera',
      sl: '/kariera',
    },
    component: asyncComponent({
      loader: () => import('./pages/CareerPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  ...(showContactPage
    ? {
        contacts: {
          path: {
            cs: '/kontakty',
            en: '/contacts',
            hu: '/kapcsolatokat',
            de: '/kontakte',
            sk: '/kontakty',
            hr: '/kontakty',
            sl: '/kontakty',
          },
          component: asyncComponent({
            loader: () => import('./pages/ContactsPage'),
            Placeholder,
          }),
          urlBuilder: routesBuilderDefaultdUrlBuilder,
        },
      }
    : {}),
  downloads: {
    path: {
      cs: '/ke-stazeni',
      en: '/downloads',
      hu: '/letoltesek',
      de: '/downloads',
      sk: '/k-stiahnutiu',
      hr: '/k-stiahnutiu',
      sl: '/k-stiahnutiu',
    },
    component: asyncComponent({
      loader: () => import('./pages/DownloadsPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  login: {
    path: {
      cs: '/prihlaseni',
      en: '/login',
      hu: '/Belépés',
      de: '/login',
      sk: '/prihlasenie',
      hr: '/prihlasenie',
      sl: '/prihlasenie',
    },
    component: asyncComponent({
      loader: () => import('./pages/LoginPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  loginSuperMaster: {
    path: {
      cs: '/prihlaseni-obchodniho-zastupce',
      en: '/login',
      hu: '/Belépés',
      de: '/login',
      sk: '/prihlasenie-obchodneho-zastupcu',
      hr: '/prihlasenie',
      sl: '/prihlasenie',
    },
    component: asyncComponent({
      loader: () => import('./pages/LoginAsSelectPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  logout: {
    path: {
      cs: '/odhlaseni',
      en: '/logout',
      hu: '/kijelentkezes',
      de: '/ausloggen',
      sk: '/odhlasenie',
      hr: '/odhlasenie',
      sl: '/odhlasenie',
    },
    component: asyncComponent({
      loader: () => import('./pages/LogoutPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  passwordRenewal: {
    path: {
      cs: '/zmena-hesla',
      en: '/change-password',
      hu: '/jelszo-modositasa',
      de: '/kennwort-ändern',
      sk: '/zmena-hesla',
      hr: '/zmena-hesla',
      sl: '/zmena-hesla',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/PasswordRenewal'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
  orderDisapprovalSending: {
    path: {
      cs: '/objednavka-zrusena',
      en: '/order-cancelled',
      hu: '/rendeles-torolve',
      de: '/bestellung-storniert',
      sk: '/objednavka-zrusena',
      hr: '/objednavka-zrusena',
      sl: '/objednavka-zrusena',
    },
    exact: true,
    component: asyncComponent({
      loader: () => import('./pages/OrderDisapprovalSendingPage'),
      Placeholder,
    }),
    urlBuilder: routesBuilderDefaultdUrlBuilder,
  },
} as const;
/**
 * Returnd builded URL from `type` and params...
 *
 * ```
 * routeUrl('home', {locale, params: { ... } }); // => '/'
 * routeUrl('productDetail', {locale, params: { ... } }); // => '/some-product-name-product-12344'
 * ```
 */

export type RouteUrlType = keyof typeof routesDefinition;

type Sep = `/` | `-` | '_' | '(' | ')' | '[' | ']' | '{' | '}' | '*' | '^';

type Split<
  T extends string,
  Separator extends string,
  Result extends string = '',
> = T extends `${infer Start}${Separator}${infer Rest}`
  ? Split<Rest, Separator, Result | Start>
  : T extends `${infer Last}`
  ? Result | Last
  : never;

type RejectWithSep<T extends string, Separator extends string> = T extends `${Separator}`
  ? never
  : T extends `:${infer Param}`
  ? Param
  : never;

type ExtractParams<T extends string> = RejectWithSep<Split<T, Sep>, Sep>;

export type ParamsDict<T extends string> = Record<ExtractParams<T>, string>;

export function routeUrl(
  type: RouteUrlType,
  opts: {
    locale: SupportedLocale;
    otherParamsToQs?: boolean | string[];
    params: Record<string, string>;
  },
): string {
  const path = routePath(type, opts);
  const def = routesDefinition[type];
  const inputParams = opts.params || {}; // ensure params exists

  return def && def.urlBuilder && typeof def.urlBuilder === 'function'
    ? def.urlBuilder(opts.locale, path, opts.otherParamsToQs, inputParams as any)
    : routesBuilderDefaultdUrlBuilder(opts.locale, path, opts.otherParamsToQs, inputParams as any);
}

export function routeIsActive(
  type: keyof typeof routesDefinition,
  opts: {
    customMatcher?: (opts: {
      nodePath: string;
      pathRegexMatches: RegExpExecArray | null;
      routeRegexMatch: boolean;
    }) => boolean;
    locale: SupportedLocale;
    match: Match<any>;
  },
): boolean {
  const nodePath = routePath(type, opts);

  const re = pathToRegexp(opts.match.path, undefined, { strict: opts.match.isExact });
  const pathRegexMatches = re.exec(nodePath);
  const routeRegexMatch = pathRegexMatches !== null;

  return opts.customMatcher ? opts.customMatcher({ nodePath, pathRegexMatches, routeRegexMatch }) : routeRegexMatch;
}

/**
 * Returns a `path` from roteDefinitions by `type` and `opts.locale`.
 *
 * ```
 * routePath('home', { locale }); // => '/'
 * routePath('productDetail', { locale }); // => '/:sn([^/]+)-product-:id([^/]+)'
 * ```
 */
export function routePath(type: keyof typeof routesDefinition, opts: { locale: SupportedLocale }): string {
  const def = routesDefinition[type];
  if (!def) return '/_error';
  return typeof def.path === 'string' ? def.path : def.path[opts.locale];
}

/**
 * Returns `inputParameters` object splitted to two separate
 * @param otherParamsToQs
 * @param path
 * @param inputParams
 */
export function routesBuilderSplitInputParams<P extends Record<string, any>>(
  locale: SupportedLocale,
  otherParamsToQs: boolean | string[] | undefined | null,
  path: string,
  inputParams: P,
): { otherParamsExists: boolean; pathParams: Record<string, any>; queryParams?: Record<string, any> } {
  const [pathParams, queryParams] = !otherParamsToQs
    ? [{ ...inputParams, locale }]
    : ((reg) => {
        const pathParamKeys = reg.keys.map((i) => i.name);
        return Object.entries(inputParams).reduce<[Record<string, any>, Record<string, any>]>(
          (acc, [k, v]) => {
            if (pathParamKeys.includes(k)) {
              return [{ ...acc[0], [k]: v }, acc[1]];
            }

            if (Array.isArray(otherParamsToQs) && !otherParamsToQs.includes(k)) {
              return acc;
            }

            return [acc[0], { ...acc[1], [k]: v }];
          },
          [{ locale }, {}],
        );
      })(pathToRegexp(path));

  const otherParamsExists = !!queryParams && Object.keys(queryParams).length > 0;

  return {
    otherParamsExists,
    pathParams,
    queryParams,
  };
}

/**
 * Returns all router routes definitions...
 */
export function routeAllRoutesToRouter(): AsyncRouteProps[] {
  const extr = ({ urlBuilder, path, ...rest }: RouteDefinition): Omit<AsyncRouteProps, 'path'> => rest;

  return Object.values(routesDefinition).reduce((acc, itm) => {
    if (typeof itm.path === 'string') return [...acc, { path: itm.path, ...extr(itm as any) }] as AsyncRouteProps[];
    return [...acc, ...Object.values(itm.path).map((path) => ({ path, ...extr(itm as any) }))] as AsyncRouteProps[];
  }, [] as AsyncRouteProps[]);
}

const safeAbsoluteUrl = (path: string, url: string): string =>
  `${path.startsWith('/') ? '/' : ''}${url.replace(/^\//, '')}`;

export interface RouteDefinition extends Omit<AsyncRouteProps, 'path'> {
  path: string | Record<SupportedLocale, string>;
  urlBuilder: (
    locale: SupportedLocale,
    path: string,
    otherParamsToQs: boolean | string[] | undefined | null,
    params: any,
  ) => string;
}
